import styled from '@emotion/styled';
import { Spacings, Fonts } from '@styles/index';
import P1 from '@base/P1/P1';
import AddedToPlanner from '@icon/Planner/AddedToPlanner';
import VerticalDots from '@icon/Planner/VerticalDots';
import { getRegularDate, getTimeFromMinutes } from '@utils/dates';
import { useState } from 'react';
import Menu from '@base/Menu/Menu';
import EditCoursePlan from '@icon/Planner/EditCoursePlan';
import DeleteFromPlanner from '@icon/Planner/DeleteFromPlanner';
import Tooltip from '@base/Tooltip/Tooltip';
import {
  ADD_TO_PLANNER_EDIT_PREFERENCES_CLICKED,
  COURSE_KEYS,
  TEST_SERIES_KEYS
} from '@constants/segment';
import { segment } from '@actions/analytics';
import { programmeTypes, evtNameCategoryMap } from '@constants/index';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useAllTypesOfCourseEventProps from '@hooks/useAllTypesOfCourseEventProps';
import EditCoursePlanTooltip from './EditCoursePlanTooltip';

const AddedToPlannerWrapper = styled.div`
  margin-top: ${Spacings.SPACING_6B};
  border-top: 1px solid var(--color-divider);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TextFlex = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-top: ${Spacings.SPACING_6B};
`;

const StyledP1 = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
  margin-left: ${Spacings.SPACING_3B};
  margin-right: ${Spacings.SPACING_4B};
`;

const LightP1 = styled(P1)`
  color: var(--color-text-secondary);
`;

const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const RightWrapper = styled.div``;

const DotIconWrapper = styled.div`
  cursor: pointer;
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  background-color: ${({ $isActive }) =>
    $isActive ? 'var(--color-divider)' : 'transparent'};
  &:hover {
    background: var(--color-divider);
  }
`;
const StyledMenu = styled(Menu)`
  padding: ${Spacings.SPACING_2B} 0;
`;
const MenuItem = styled.div`
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const StyledEditText = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
  margin-left: ${Spacings.SPACING_2B};
`;

const StyledRemoveText = styled(P1)`
  font-weight: ${Fonts.SEMIBOLD};
  margin-left: ${Spacings.SPACING_2B};
  color: var(--color-i-red);
`;

const StyTooltip = styled(Tooltip)`
  &.EditCoursePlanTooltip.MuiTooltip-popper {
    z-index: 1;
  }
`;

const AddedToPlannerCard = ({
  show = false,
  hide,
  dailyItemScheduleAt,
  startDate,
  handleRemoveFromPlanner,
  handleEditCoursePlan,
  goalUid,
  plusCourse,
  courseType
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const goalSegmentData = useGoalEventProps({ goalUID: goalUid });
  const segmentData = useAllTypesOfCourseEventProps({ course: plusCourse });
  const eventKeys =
    courseType === programmeTypes.TESTSERIES ? TEST_SERIES_KEYS : COURSE_KEYS;
  const handleMenuClose = () => setAnchorEl(null);
  const handleMenuClick = (e) => {
    hide();
    setAnchorEl(e.currentTarget);
  };

  const removeFromPlannerClick = async () => {
    setAnchorEl(null);
    handleRemoveFromPlanner(true);
  };
  const editCoursePlanClick = () => {
    handleEditCoursePlan();
    setAnchorEl(null);
    segment.track(
      ADD_TO_PLANNER_EDIT_PREFERENCES_CLICKED,
      { ...goalSegmentData, ...segmentData },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      eventKeys
    );
  };

  return (
    <AddedToPlannerWrapper>
      <TextFlex>
        <LeftWrapper>
          <AddedToPlanner size={32} />
          <StyledP1>Added to your planner</StyledP1>
          {dailyItemScheduleAt && (
            <LightP1>
              {`From ${getRegularDate(
                startDate
              )} · 1 class every day at ${getTimeFromMinutes(
                dailyItemScheduleAt
              )}`}
            </LightP1>
          )}
        </LeftWrapper>
        <RightWrapper>
          <StyTooltip
            arrow
            open={show}
            title={<EditCoursePlanTooltip onClose={() => hide(false)} />}
            placement="top-end"
            size="295px"
            borderRadius={Spacings.SPACING_2B}
            maxWidth="295px"
            backgroundColor="var(--color-base-fill)"
            className="EditCoursePlanTooltip"
            popperProps={{
              modifiers: {
                flip: {
                  enabled: false
                }
              }
            }}
          >
            <DotIconWrapper onClick={handleMenuClick} $isActive={!!anchorEl}>
              <VerticalDots size="24px" color="var(--color-text-secondary)" />
            </DotIconWrapper>
          </StyTooltip>
          <StyledMenu
            anchorEl={anchorEl}
            onClose={handleMenuClose}
            position="right"
            height="auto"
            width={206}
          >
            <MenuItem onClick={editCoursePlanClick}>
              <EditCoursePlan size="20px" />
              <StyledEditText>Edit course plan</StyledEditText>
            </MenuItem>
            <MenuItem onClick={removeFromPlannerClick}>
              <DeleteFromPlanner size="24px" />
              <StyledRemoveText>Remove from planner</StyledRemoveText>
            </MenuItem>
          </StyledMenu>
        </RightWrapper>
      </TextFlex>
    </AddedToPlannerWrapper>
  );
};

export default AddedToPlannerCard;
